/**
 * useOfflineFormQueue — Operational forms offline queue hook
 *
 * Preserves the existing SmartTask/forms hook API while delegating persistence
 * to the unified encrypted IndexedDB offline store.
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  emitOperationsSyncActivity,
  OPERATIONS_SYNC_PENDING_CHANGED_EVENT,
} from '@/lib/operations/offlineSyncEvents';
import {
  cacheOfflineFormConfig,
  clearOfflineFormDraft,
  clearOfflineFormQueue,
  getCachedOfflineFormConfig,
  getOfflineFormDraft,
  getOfflineFormQueuePendingCount,
  getQueuedOfflineFormSubmissions,
  predownloadOperationalFormConfigs,
  queueOfflineFormSubmission,
  saveOfflineFormDraft,
  syncOfflineFormQueueNow,
  type OperationalFormWorkItemTarget,
  type QueuedSubmission,
  type StoredFormDraft,
} from '@/lib/offline/operationalFormsStore';

export type {
  OperationalFormWorkItemTarget,
  QueuedSubmission,
  StoredFormDraft,
} from '@/lib/offline/operationalFormsStore';

export {
  cacheOfflineFormConfig,
  clearOfflineFormDraft,
  getCachedOfflineFormConfig,
  getOfflineFormDraft,
  getOfflineFormQueuePendingCount,
  predownloadOperationalFormConfigs,
  queueOfflineFormSubmission,
  saveOfflineFormDraft,
  syncOfflineFormQueueNow,
} from '@/lib/offline/operationalFormsStore';

export interface UseOfflineFormQueueResult {
  isOnline: boolean;
  pendingCount: number;
  pendingSubmissions: QueuedSubmission[];
  isSyncing: boolean;
  cacheFormConfig: (formKey: string, config: unknown) => Promise<void>;
  getCachedFormConfig: <T = unknown>(formKey: string) => Promise<T | null>;
  queueSubmission: (sub: Omit<QueuedSubmission, 'id' | 'queuedAt' | 'retries'>) => Promise<void>;
  saveDraft: (draft: Omit<StoredFormDraft, 'savedAt'>) => Promise<void>;
  getDraft: (draftId: string) => Promise<StoredFormDraft | null>;
  clearDraft: (draftId: string) => Promise<void>;
  syncNow: () => Promise<{ synced: number; failed: number }>;
  clearQueue: () => Promise<void>;
}

export function useOfflineFormQueue(): UseOfflineFormQueueResult {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [pendingSubmissions, setPendingSubmissions] = useState<QueuedSubmission[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncingRef = useRef(false);

  const refreshQueue = useCallback(async () => {
    const items = await getQueuedOfflineFormSubmissions();
    setPendingSubmissions(items);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    const handlePendingChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ source?: string }>).detail;
      if (!detail?.source || detail.source === 'forms') {
        void refreshQueue();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener(OPERATIONS_SYNC_PENDING_CHANGED_EVENT, handlePendingChanged as EventListener);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener(OPERATIONS_SYNC_PENDING_CHANGED_EVENT, handlePendingChanged as EventListener);
    };
  }, [refreshQueue]);

  useEffect(() => {
    void refreshQueue();
  }, [refreshQueue]);

  const cacheFormConfig = useCallback(async (formKey: string, config: unknown) => {
    await cacheOfflineFormConfig(formKey, config);
  }, []);

  const getCachedFormConfig = useCallback(async <T = unknown>(formKey: string): Promise<T | null> => {
    return getCachedOfflineFormConfig<T>(formKey);
  }, []);

  const queueSubmission = useCallback(async (sub: Omit<QueuedSubmission, 'id' | 'queuedAt' | 'retries'>) => {
    await queueOfflineFormSubmission(sub);
    await refreshQueue();
  }, [refreshQueue]);

  const saveDraft = useCallback(async (draft: Omit<StoredFormDraft, 'savedAt'>) => {
    await saveOfflineFormDraft(draft);
  }, []);

  const getDraft = useCallback(async (draftId: string): Promise<StoredFormDraft | null> => {
    return getOfflineFormDraft(draftId);
  }, []);

  const clearDraft = useCallback(async (draftId: string) => {
    await clearOfflineFormDraft(draftId);
  }, []);

  const syncNow = useCallback(async (): Promise<{ synced: number; failed: number }> => {
    if (syncingRef.current || !isOnline) {
      return { synced: 0, failed: 0 };
    }

    syncingRef.current = true;
    setIsSyncing(true);

    emitOperationsSyncActivity({
      source: 'forms',
      phase: 'start',
      message: 'Syncing offline forms...',
    });

    try {
      const result = await syncOfflineFormQueueNow();

      if (result.failed > 0 && result.synced === 0) {
        emitOperationsSyncActivity({
          source: 'forms',
          phase: 'error',
          message: 'Offline form sync failed',
          error: `${result.failed} form submission(s) still pending`,
        });
      } else {
        emitOperationsSyncActivity({
          source: 'forms',
          phase: 'complete',
          message: result.synced > 0
            ? `Synced ${result.synced} offline form${result.synced === 1 ? '' : 's'}`
            : 'Offline forms are up to date',
        });
      }

      return { synced: result.synced, failed: result.failed };
    } catch (error: unknown) {
      emitOperationsSyncActivity({
        source: 'forms',
        phase: 'error',
        message: 'Offline form sync failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return { synced: 0, failed: pendingSubmissions.length };
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
      await refreshQueue();
    }
  }, [isOnline, pendingSubmissions.length, refreshQueue]);

  useEffect(() => {
    if (isOnline && pendingSubmissions.length > 0 && !syncingRef.current) {
      const timer = setTimeout(() => {
        void syncNow();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, pendingSubmissions.length, syncNow]);

  const clearQueue = useCallback(async () => {
    await clearOfflineFormQueue();
    await refreshQueue();
  }, [refreshQueue]);

  return {
    isOnline,
    pendingCount: pendingSubmissions.length,
    pendingSubmissions,
    isSyncing,
    cacheFormConfig,
    getCachedFormConfig,
    queueSubmission,
    saveDraft,
    getDraft,
    clearDraft,
    syncNow,
    clearQueue,
  };
}

export default useOfflineFormQueue;
